import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import type { FieldDefinition, FieldOption, FieldValueRow } from './fieldChips';

/**
 * A project's custom fields, and what every task on it holds.
 *
 * Two calls per board, not two per card. project_task_field_values returns the
 * whole board's values in one round trip precisely so a board of two hundred
 * cards does not open two hundred connections — which is what a naive
 * per-card hook would do, and would only become obvious once the Social team
 * had a real backlog in here.
 */

export const fieldKeys = {
  all: ['project-fields'] as const,
  definitions: (projectId: string) => [...fieldKeys.all, 'definitions', projectId] as const,
  values: (projectId: string) => [...fieldKeys.all, 'values', projectId] as const,
};

async function fetchDefinitions(
  client: SupabaseClient,
  projectId: string,
): Promise<FieldDefinition[]> {
  const { data, error } = await client.rpc('project_field_definitions', { p_project: projectId });
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => ({
    field_id: row.field_id,
    name: row.name,
    sort_order: row.sort_order,
    is_multi: row.is_multi,
    // options comes back as jsonb; the generated type cannot describe a
    // jsonb_agg. Narrowed rather than trusted, so a row with an unexpected
    // shape renders an empty dropdown instead of throwing on .map().
    options: Array.isArray(row.options) ? (row.options as unknown as FieldOption[]) : [],
  }));
}

async function fetchValues(client: SupabaseClient, projectId: string): Promise<FieldValueRow[]> {
  const { data, error } = await client.rpc('project_task_field_values', { p_project: projectId });
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export function useProjectFields(projectId: string) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: fieldKeys.definitions(projectId),
    queryFn: () => fetchDefinitions(client, projectId),
    enabled: access.status === 'approved' && projectId !== '',
    retry: false,
  });
}

export function useProjectFieldValues(projectId: string) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: fieldKeys.values(projectId),
    queryFn: () => fetchValues(client, projectId),
    enabled: access.status === 'approved' && projectId !== '',
    retry: false,
  });
}

/**
 * Set or clear one value.
 *
 * One mutation for both, because from a card's point of view they are one
 * gesture: clicking an option. Which of the two it turns into is decided by
 * toggleAction, beside the rest of the field rules, rather than by whichever
 * component happens to be rendering the menu.
 */
export function useSetTaskField(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      taskId: string;
      fieldId: string;
      action: { kind: 'set'; optionId: string } | { kind: 'clear'; optionId: string | null };
    }) => {
      const { error } =
        input.action.kind === 'set'
          ? await client.rpc('set_task_field', {
              p_task: input.taskId,
              p_field: input.fieldId,
              p_option: input.action.optionId,
            })
          : await client.rpc('clear_task_field', {
              p_task: input.taskId,
              p_field: input.fieldId,
              // Null clears the whole field. The generated type cannot express
              // a nullable argument, so it is narrowed at this one boundary
              // rather than by widening the shared shape.
              p_option: input.action.optionId as string,
            });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldKeys.values(projectId) });
    },
  });
}
